import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./AdminDashboard.scss";
import IconDashboard from "../../common/icons/IconDashboard";
import IconClose from "../../common/icons/IconClose";
import IconRequest from "../../common/icons/IconRequest";
import IconCost from "../../common/icons/IconCost";
import ShowDetails from "../show-details/ShowDetails";
import useGraphData from "../../hooks/graph";
import Graph from "./Graph";
import RequestsPerOriginChart from "./RequestsPerOrigin";
import { AI_BASE_URL, API_BASE_URL } from "../../config/index";
import { CSVLink } from "react-csv";
import debounce from "lodash.debounce";

// Load environment variables

const AdminDashboard = ({ token, user }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 20,
    hasNext: false,
    hasPrev: false,
  });
  const [userData, setUserData] = useState({
    totalRequests: 0,
    totalCost: 0,
    averageCost: 0,
    allRequests: [],
    costStatistics: {
      filtered_total_cost: 0,
      filtered_average_cost: 0,
      filtered_min_cost: 0,
      filtered_max_cost: 0,
      filtered_count_with_cost: 0,
      filtered_count_without_cost: 0,
      all_records_total_cost: 0,
      all_records_average_cost: 0,
      all_records_count_with_cost: 0,
    },
  });
  const [uniqueUserCount, setUniqueUserCount] = useState(0);
  const [userNames, setUserNames] = useState({});
  const [userMails, setUserMails] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    dateRange: { startDate: null, endDate: null },
    userId: "", // User ID filter for the /filter endpoint
    userName: "", // User name filter (will be converted to userId)
    rating: "", // Rating filter (1-5 stars)
  });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [activeTab, setActiveTab] = useState("Statistiques");

  const handleRowClick = (req) => {
    const formattedData = {
      body: {
        answer: req.answer,
        answered: true,
        sources: req.sources || [],
        input: req.question,
      },
    };

    setSelectedRequest(formattedData);
  };

  // Find user ID by name (case-insensitive partial match)
  const findUserIdByName = (nameQuery, userNamesObj = userNames) => {
    if (!nameQuery || !nameQuery.trim()) return null;

    const query = nameQuery.toLowerCase().trim();

    // Find user ID where the name contains the search query
    for (const [userId, userName] of Object.entries(userNamesObj)) {
      if (userName.toLowerCase().includes(query)) {
        return parseInt(userId, 10);
      }
    }

    return null;
  };

  // Build query parameters for API calls
  const buildQueryParams = (
    page = paginationInfo.currentPage,
    size = paginationInfo.pageSize,
    currentFilter = filter,
    userNamesObj = userNames
  ) => {
    const params = new URLSearchParams();

    params.append("page", page.toString());
    params.append("size", size.toString());

    // Determine which user ID to use - direct userId or userName converted to userId
    let effectiveUserId = null;

    if (currentFilter.userId && currentFilter.userId.trim()) {
      const userIdInt = parseInt(currentFilter.userId.trim(), 10);
      if (!isNaN(userIdInt) && userIdInt > 0) {
        effectiveUserId = userIdInt;
      }
    } else if (currentFilter.userName && currentFilter.userName.trim()) {
      effectiveUserId = findUserIdByName(currentFilter.userName, userNamesObj);
      if (effectiveUserId) {
      }
    }

    if (effectiveUserId) {
      params.append("user_id", effectiveUserId.toString());
    }

    // Add rating filter
    if (currentFilter.rating && currentFilter.rating.trim()) {
      const ratingInt = parseInt(currentFilter.rating.trim(), 10);
      if (!isNaN(ratingInt) && ratingInt >= 1 && ratingInt <= 5) {
        params.append("rating", ratingInt.toString());
      }
    }

    if (currentFilter.dateRange.startDate) {
      // Format date as YYYY-MM-DD
      const startDate = currentFilter.dateRange.startDate
        .toISOString()
        .split("T")[0];
      params.append("start_date", startDate);
    }
    if (currentFilter.dateRange.endDate) {
      // Format date as YYYY-MM-DD
      const endDate = currentFilter.dateRange.endDate
        .toISOString()
        .split("T")[0];
      params.append("end_date", endDate);
    }

    return params.toString();
  };

  const fetchUserReviewsWithFilter = async (
    page = 1,
    size = 20,
    currentFilter = filter,
    userNamesObj = userNames
  ) => {
    setLoading(true);
    try {
      // Use the new /filter endpoint
      const url = `${AI_BASE_URL}/reviews/filter`;
      const queryParams = buildQueryParams(
        page,
        size,
        currentFilter,
        userNamesObj
      );
      const fullUrl = `${url}?${queryParams}`;

      const response = await axios.get(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;

      // The /filter endpoint returns a structured response
      const reviewsData = data.data || [];
      const paginationData = {
        total: data.total || 0,
        totalPages: data.total_pages || 0,
        currentPage: data.page || page,
        pageSize: data.size || size,
        hasNext: data.has_next || false,
        hasPrev: data.has_prev || false,
      };

      setReviews(reviewsData);
      setPaginationInfo(paginationData);

      // Use cost statistics from the API response
      const totalRequests = paginationData.total;
      const costStats = data.cost_statistics || {
        filtered_total_cost: 0,
        filtered_average_cost: 0,
        filtered_min_cost: 0,
        filtered_max_cost: 0,
        filtered_count_with_cost: 0,
        filtered_count_without_cost: 0,
        all_records_total_cost: 0,
        all_records_average_cost: 0,
        all_records_count_with_cost: 0,
      };

      // Calculate unique users from current page data
      const uniqueUserIds = new Set(
        reviewsData.map((review) => review.user_id)
      );
      setUniqueUserCount(uniqueUserIds.size);

      // Fetch user names for current page
      if (uniqueUserIds.size > 0) {
        await fetchUserNames(uniqueUserIds);
      }

      setUserData({
        totalRequests,
        totalCost: costStats.filtered_total_cost,
        averageCost: costStats.filtered_average_cost,
        allRequests: reviewsData,
        costStatistics: costStats,
      });
    } catch (error) {
      console.error("Error fetching user reviews:", error);
      // Reset on error
      setReviews([]);
      setPaginationInfo({
        total: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 20,
        hasNext: false,
        hasPrev: false,
      });
      setUserData({
        totalRequests: 0,
        totalCost: 0,
        averageCost: 0,
        allRequests: [],
        costStatistics: {
          filtered_total_cost: 0,
          filtered_average_cost: 0,
          filtered_min_cost: 0,
          filtered_max_cost: 0,
          filtered_count_with_cost: 0,
          filtered_count_without_cost: 0,
          all_records_total_cost: 0,
          all_records_average_cost: 0,
          all_records_count_with_cost: 0,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserReviews = async (page = 1, size = 20) => {
    return fetchUserReviewsWithFilter(page, size, filter, userNames);
  };

  const fetchUserNames = async (userIds) => {
    try {
      const nameMap = {};
      const mailsMap = {};
      await Promise.all(
        [...userIds]
          .filter((userId) => userId !== null)
          .map(async (userId) => {
            try {
              const response = await axios.get(
                `${API_BASE_URL}/organization/user/${userId}`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );
              mailsMap[userId] = response.data.data.mainEmail;
              nameMap[userId] =
                response.data.data.firstName +
                " " +
                response.data.data.lastName;
            } catch (error) {
              console.warn(`Failed to fetch user ${userId}:`, error);
              nameMap[userId] = `User ${userId}`;
              mailsMap[userId] = "Unknown";
            }
          })
      );
      setUserNames((prev) => ({ ...prev, ...nameMap }));
      setUserMails((prev) => ({ ...prev, ...mailsMap }));
    } catch (error) {
      console.error("Error fetching user names:", error);
    }
  };

  // Debounced version of fetchUserReviews for filter changes
  const debouncedFetchUserReviews = useCallback(
    (() => {
      let timeoutId;
      return (
        page = 1,
        size = 20,
        currentFilter = filter,
        userNamesObj = userNames
      ) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          // Call fetchUserReviews with the captured filter state and userNames
          fetchUserReviewsWithFilter(page, size, currentFilter, userNamesObj);
        }, 500); // 500ms delay
      };
    })(),
    [] // Remove dependencies to prevent recreation
  );

  // Fetch data when component opens
  useEffect(() => {
    if (isOpen) {
      fetchUserReviews(1, paginationInfo.pageSize);
    }
  }, [isOpen]);

  // Fetch data when filters change (with debouncing for userId)
  useEffect(() => {
    if (isOpen) {
      const hasDateFilters =
        filter.dateRange.startDate || filter.dateRange.endDate;
      const hasValidUserId =
        filter.userId &&
        filter.userId.trim() &&
        !isNaN(parseInt(filter.userId.trim(), 10)) &&
        parseInt(filter.userId.trim(), 10) > 0;
      const hasValidUserName =
        filter.userName &&
        filter.userName.trim() &&
        findUserIdByName(filter.userName, userNames);
      const hasRatingFilter =
        filter.rating &&
        filter.rating.trim() &&
        !isNaN(parseInt(filter.rating.trim(), 10)) &&
        parseInt(filter.rating.trim(), 10) >= 1 &&
        parseInt(filter.rating.trim(), 10) <= 5;

      // If we have date or rating filters, fetch immediately (regardless of userId)
      if (hasDateFilters || hasRatingFilter) {
        fetchUserReviews(1, paginationInfo.pageSize);
      }
      // If we only have userId or userName filter (no dates/rating), use debounced version
      else if (hasValidUserId || hasValidUserName) {
        const userIdentifier = hasValidUserId
          ? parseInt(filter.userId.trim(), 10)
          : findUserIdByName(filter.userName, userNames);
        debouncedFetchUserReviews(
          1,
          paginationInfo.pageSize,
          filter,
          userNames
        );
      }
      // If no filters at all, fetch immediately
      else if (
        !filter.userId &&
        !filter.userName &&
        !hasDateFilters &&
        !hasRatingFilter
      ) {
        fetchUserReviews(1, paginationInfo.pageSize);
      }
      // If userId, userName, or rating is invalid, don't fetch
      else if (
        (filter.userId && filter.userId.trim()) ||
        (filter.userName && filter.userName.trim()) ||
        (filter.rating && filter.rating.trim())
      ) {
      }
    }
  }, [filter, isOpen]);

  // Handle page changes
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= paginationInfo.totalPages) {
      fetchUserReviews(newPage, paginationInfo.pageSize);
    }
  };

  // Handle page size changes
  const handlePageSizeChange = (newSize) => {
    setPaginationInfo((prev) => ({ ...prev, pageSize: newSize }));
    fetchUserReviews(1, newSize);
  };

  // Filter change handlers with debouncing
  const handleFilterChange = (key, value) => {
    setFilter((prev) => {
      const newFilter = { ...prev, [key]: value };

      // Clear the other user filter when one is being used
      if (key === "userId" && value.trim()) {
        newFilter.userName = "";
      } else if (key === "userName" && value.trim()) {
        newFilter.userId = "";
      }

      return newFilter;
    });
    // Reset to first page when filter changes
    setPaginationInfo((prev) => ({ ...prev, currentPage: 1 }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("fr-BE", {
      day: "2-digit",
      month: "long",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const renderStars = (rating) => {
    const totalStars = 5;
    return (
      <div className="star-rating">
        {Array.from({ length: totalStars }).map((_, index) => (
          <svg
            key={index}
            fill={index < rating ? "rgb(24, 160, 251)" : "#ccc"}
            width="22"
            height="22"
            viewBox="0 0 24 24"
            className="star-svg"
          >
            <path
              fill={index < rating ? "rgb(24, 160, 251)" : "#ccc"}
              stroke="none"
              strokeMiterlimit="10"
              strokeWidth="0"
              d="M12,17.27L18.18,21l-1.64-7.03L22,9.24l-7.19-0.61L12,2L9.19,8.63L2,9.24l5.46,4.73L5.82,21L12,17.27z"
            ></path>
          </svg>
        ))}
      </div>
    );
  };

  // Calculate statistics from API response (not from current page data)
  const totalRequests = paginationInfo.total;
  const totalCost = userData.totalCost || 0;
  const averageCost = userData.averageCost || 0;

  const handlePreviousPage = () => {
    if (paginationInfo.hasPrev) {
      handlePageChange(paginationInfo.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationInfo.hasNext) {
      handlePageChange(paginationInfo.currentPage + 1);
    }
  };

  const usersPerDate = useGraphData(
    `${AI_BASE_URL}/reviews/users-per-date`,
    token
  );
  const newUsersPerDate = useGraphData(
    `${AI_BASE_URL}/reviews/new-users-per-date`,
    token
  );
  const requestsPerDate = useGraphData(
    `${AI_BASE_URL}/reviews/requests-per-date`,
    token
  );
  const requestsPerOrigin = useGraphData(
    `${AI_BASE_URL}/reviews/requests-per-origin`,
    token
  );

  const formatCost = (cost) => {
    if (cost === null || cost === undefined) return "0.00000";
    if (typeof cost !== "number") return "0.00000";
    return cost.toFixed(5);
  };

  const [timeFilter, setTimeFilter] = useState("day");

  // CSV export data
  const headers = [
    { label: "Utilisateur", key: "user_id" },
    { label: "Email", key: "email" },
    { label: "Question", key: "question" },
    { label: "Réponse", key: "answer" },
    { label: "Coût", key: "cost" },
    { label: "Model", key: "model" },
    { label: "Origine", key: "origin" },
    { label: "Évaluation", key: "rating" },
    { label: "Destinataires", key: "recipient_emails" },
    { label: "Date", key: "created_at" },
  ];

  const formattedData = Array.isArray(reviews)
    ? reviews.map((item) => ({
        user_id: userNames[item.user_id] || `User ${item.user_id}`,
        email: userMails[item.user_id] || "Unknown",
        question: item.question || "",
        answer: item.answer?.replace(/\n/g, " ").replace(";", ",") ?? "",
        cost: item.cost?.toFixed(6) ?? "0.000000",
        model: item.model ?? "",
        origin: item.origin ?? "",
        rating: item.rating ?? "",
        recipient_emails: Array.isArray(item.recipient_emails)
          ? item.recipient_emails.join(", ")
          : "",
        created_at: new Date(item.created_at).toLocaleString("fr-FR"),
      }))
    : [];

  const debouncedSearch = useMemo(
    () =>
      debounce((attribute, val) => {
        handleFilterChange(attribute, val);
      }, 500),
    []
  );

  const handleChange = (value, attribute) => {
    setFilter((prev) => ({
      ...prev,
      [attribute]: value,
    }));

    debouncedSearch(attribute, value);
  };

  return (
    <div>
      <button className="floating-button" onClick={() => setIsOpen(!isOpen)}>
        <IconDashboard />
      </button>

      {isOpen && (
        <>
          <div className="overlay" onClick={() => setIsOpen(false)}></div>
          <div className="panel">
            <button className="close-button" onClick={() => setIsOpen(false)}>
              <IconClose />
            </button>

            <h1>Tableau de Bord</h1>

            <div className="tabs">
              <button
                className={activeTab === "Statistiques" ? "active" : ""}
                onClick={() => setActiveTab("Statistiques")}
              >
                Statistiques
              </button>
              <button
                className={activeTab === "Tendances" ? "active" : ""}
                onClick={() => setActiveTab("Tendances")}
              >
                Tendances
              </button>
            </div>

            {activeTab === "Tendances" && (
              <>
                <div className="time-filters">
                  <select
                    className="filter-dropdown"
                    value={timeFilter}
                    onChange={(e) => setTimeFilter(e.target.value)}
                  >
                    <option value="day">Par jour</option>
                    <option value="month">Par mois</option>
                  </select>
                </div>
                <div className="graphs">
                  <Graph
                    title="Utilisateurs par date"
                    data={usersPerDate.data}
                    timeFilter={timeFilter}
                  />
                  <Graph
                    title="Nouveaux utilisateurs par date"
                    data={newUsersPerDate.data}
                    timeFilter={timeFilter}
                  />
                  <Graph
                    title="Requêtes par date"
                    data={requestsPerDate.data}
                    timeFilter={timeFilter}
                  />
                  <RequestsPerOriginChart
                    title="Requêtes par origine"
                    data={requestsPerOrigin.data}
                  />
                </div>
              </>
            )}

            {/* Filter Section */}
            {activeTab === "Statistiques" && (
              <>
                <div className="filters">
                  <input
                    type="number"
                    placeholder="ID Utilisateur"
                    value={filter.userId}
                    onChange={(e) => handleChange(e.target.value, "userId")}
                    min="1"
                    step="1"
                  />
                  <input
                    type="text"
                    placeholder="Utilisateur"
                    value={filter.userName}
                    onChange={(e) => handleChange(e.target.value, "userName")}
                  />
                  <select
                    value={filter.rating}
                    onChange={(e) =>
                      handleFilterChange("rating", e.target.value)
                    }
                  >
                    <option value="">Toutes évaluations</option>
                    <option value="1">1 étoile</option>
                    <option value="2">2 étoiles</option>
                    <option value="3">3 étoiles</option>
                    <option value="4">4 étoiles</option>
                    <option value="5">5 étoiles</option>
                  </select>
                  <DatePicker
                    selected={filter.dateRange.startDate}
                    onChange={(date) =>
                      setFilter((prev) => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, startDate: date },
                      }))
                    }
                    placeholderText="Date Début"
                    dateFormat="yyyy-MM-dd"
                    maxDate={new Date()}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                  />
                  <DatePicker
                    selected={filter.dateRange.endDate}
                    onChange={(date) =>
                      setFilter((prev) => ({
                        ...prev,
                        dateRange: { ...prev.dateRange, endDate: date },
                      }))
                    }
                    placeholderText="Date Fin"
                    dateFormat="yyyy-MM-dd"
                    maxDate={new Date()}
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                  />
                </div>

                <div className="stats">
                  <div className="stat-card">
                    <div className="card-content">
                      <div>
                        <h4>Nombre d'utilisateurs</h4>
                        <p>{uniqueUserCount}</p>
                      </div>
                      <div className="icon-container">
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 7C9.65685 7 11 5.65685 11 4C11 2.34315 9.65685 1 8 1C6.34315 1 5 2.34315 5 4C5 5.65685 6.34315 7 8 7Z"
                            fill="currentColor"
                          />
                          <path
                            d="M14 12C14 10.3431 12.6569 9 11 9H5C3.34315 9 2 10.3431 2 12V15H14V12Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="card-content">
                      <div>
                        <h4>Nombre de requêtes</h4>
                        <p>{totalRequests}</p>
                      </div>
                      <div className="icon-container">
                        <IconRequest />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="card-content">
                      <div>
                        <h4>Coût total (filtré)</h4>
                        <p>{totalCost.toFixed(4)}$</p>
                      </div>
                      <div className="icon-container">
                        <IconCost />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="card-content">
                      <div>
                        <h4>Coût total</h4>
                        <p>
                          {(
                            userData.costStatistics?.all_records_total_cost || 0
                          ).toFixed(4)}
                          $
                        </p>
                      </div>
                      <div className="icon-container">
                        <IconCost />
                      </div>
                    </div>
                  </div>
                  <div className="stat-card">
                    <div className="card-content">
                      <div>
                        <h4>Coût moyen par requête (filtré)</h4>
                        <p>{averageCost.toFixed(4)}$</p>
                      </div>
                      <div className="icon-container">
                        <IconCost />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <br />

            {/* Page Size Selector */}
            <div className="table-controls">
              <div className="page-size-selector">
                <label>Résultats par page: </label>
                <select
                  value={paginationInfo.pageSize}
                  onChange={(e) =>
                    handlePageSizeChange(parseInt(e.target.value))
                  }
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              {/* Loading indicator */}
              {loading && (
                <div className="loading-indicator">Chargement...</div>
              )}
            </div>

            {/* Requests Table */}
            <div className="table-header">
              <h3>
                Requêtes ({paginationInfo.total} total
                {paginationInfo.total !== 1 ? "s" : ""})
              </h3>
              <CSVLink
                data={formattedData}
                headers={headers}
                filename="reviews.csv"
                separator=";"
                enclosingCharacter={`"`}
                utf8WithBom={true}
              >
                <button className="export-button" disabled={loading}>
                  Export as CSV
                </button>
              </CSVLink>
            </div>

            <table className="requests-table">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Question</th>
                  <th>Réponse</th>
                  <th>Coût</th>
                  <th>Model</th>
                  <th>Origine</th>
                  <th>Évaluation</th>
                  <th>Destinataires</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      Chargement...
                    </td>
                  </tr>
                ) : reviews.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      style={{ textAlign: "center", padding: "20px" }}
                    >
                      Aucune requête trouvée
                    </td>
                  </tr>
                ) : (
                  reviews.map((req) => (
                    <tr key={req.id} onClick={() => handleRowClick(req)}>
                      <td>{userNames[req.user_id] || `User ${req.user_id}`}</td>
                      <td>
                        <p className="answer">{req.question}</p>
                      </td>
                      <td>
                        <p className="answer">{req.answer}</p>
                      </td>
                      <td>{formatCost(req.cost)}$</td>
                      <td>{req.model}</td>
                      <td>{req.origin}</td>
                      <td>{renderStars(req.rating)}</td>
                      <td>
                        {req.recipient_emails
                          ? req.recipient_emails.join(", ")
                          : "-"}
                      </td>
                      <td>{formatDate(req.updated_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Enhanced Pagination */}
            <div className="pagination">
              <div className="pagination-info">
                Affichage{" "}
                {(paginationInfo.currentPage - 1) * paginationInfo.pageSize + 1}{" "}
                à{" "}
                {Math.min(
                  paginationInfo.currentPage * paginationInfo.pageSize,
                  paginationInfo.total
                )}{" "}
                sur {paginationInfo.total} résultats
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(1)}
                  disabled={paginationInfo.currentPage === 1 || loading}
                >
                  Premier
                </button>
                <button
                  className="pagination-button"
                  onClick={() =>
                    handlePageChange(
                      Math.max(1, paginationInfo.currentPage - 5)
                    )
                  }
                  disabled={paginationInfo.currentPage <= 5 || loading}
                >
                  « 5
                </button>
                <button
                  className="pagination-button"
                  onClick={handlePreviousPage}
                  disabled={!paginationInfo.hasPrev || loading}
                >
                  Précédent
                </button>
                <span className="page-info">
                  Page {paginationInfo.currentPage} sur{" "}
                  {paginationInfo.totalPages}
                </span>
                <button
                  className="pagination-button"
                  onClick={handleNextPage}
                  disabled={!paginationInfo.hasNext || loading}
                >
                  Suivant
                </button>
                <button
                  className="pagination-button"
                  onClick={() =>
                    handlePageChange(
                      Math.min(
                        paginationInfo.totalPages,
                        paginationInfo.currentPage + 5
                      )
                    )
                  }
                  disabled={
                    paginationInfo.currentPage >=
                      paginationInfo.totalPages - 4 || loading
                  }
                >
                  5 »
                </button>
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(paginationInfo.totalPages)}
                  disabled={
                    paginationInfo.currentPage === paginationInfo.totalPages ||
                    loading
                  }
                >
                  Dernier
                </button>
              </div>
            </div>

            {selectedRequest && (
              <ShowDetails
                token={token}
                data={selectedRequest}
                user={user}
                onClose={() => setSelectedRequest(null)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
